<template>
  <v-btn
    v-set-test-id:button="['BaseButton', testId || $attrs.to || $attrs.href]"
    v-bind="mergeProps($attrs, attrs)"
    @click="onClick"
  >
    <font-awesome-icon
      v-if="icons.left"
      :icon="icons.left"
      :spin="spin || loading"
      size="lg"
      :class="{ 'mr-3': slots.default }"
    />
    <span ref="content">
      <slot name="default" />
    </span>
    <font-awesome-icon
      v-if="icons.right"
      :icon="icons.right"
      size="lg"
      :class="{ 'mr-3': slots.default, 'ml-3': slots.default || icons.left }"
    />
  </v-btn>
</template>

<script lang="ts" setup>
import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons';
import {
  computed,
  getCurrentInstance,
  mergeProps,
  onMounted,
  ref,
  useSlots,
} from 'vue';
import { useDisplay } from 'vuetify';
import type { ActionableComponentProps } from '@/common/components/ActionableComponent';
import { vSetTestId } from '@/common/components/ActionableComponent';
import getMatomo from '@/common/composition/getMatomo';

type Props = ActionableComponentProps & {
  icon?: IconDefinition;
  iconRight?: IconDefinition;
  loading?: boolean;
  spin?: boolean;
  primary?: boolean;
  trackName?: string;
  color?: string;
  variant?: 'flat' | 'elevated' | 'tonal' | 'outlined' | 'text' | 'plain';
  size?: 'large' | 'default' | 'small';
};
const props = defineProps<Props>();
const slots = useSlots();
type Emits = { (e: 'click', v: Event): void };
const emit = defineEmits<Emits>();

const icons = computed<{ left?: IconDefinition; right?: IconDefinition }>(
  () => {
    if (props.loading && props.iconRight && !props.icon) {
      return { right: faSpinner };
    }
    return {
      left: props.loading ? faSpinner : props.icon,
      right: props.iconRight,
    };
  },
);

const display = useDisplay();
const attrs = computed(() => ({
  variant: props.variant || (props.primary ? 'elevated' : 'outlined'),
  color: props.color || 'primary',
  size: props.size || (display.xs.value ? 'default' : 'large'),
  icon: undefined,
}));

const content = ref<HTMLSpanElement>();

// Matomo tracking
let trackClick: (name: string) => void | undefined;
onMounted(() => {
  const self = getCurrentInstance();
  trackClick = name => {
    getMatomo(self)?.trackEvent('button', 'click', name);
  };
});

const onClick = (event: Event) => {
  if (props.loading) {
    event.preventDefault();
    return;
  }

  emit('click', event);

  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  trackClick &&
    trackClick(props.trackName || content.value?.innerText || 'unknown');
};
</script>
